import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { ErrorOutlineOutlined } from '@mui/icons-material';
import { Grid } from '@mui/material';

import * as colors from 'commons/styles/colors';
import { ISTOPickItemWithSummary } from 'commons/types';
import {
  completeSTOPickAction,
  getSTOAssignedPickItems,
} from 'redux-stores/actions';
import { RootReducerInterface } from 'redux-stores/reducers';
import { AppDispatch } from 'redux-stores/store';
import { STO_PICK_ROUTES } from 'routes/constants';

import { STOSkuList } from './components/STOSkuList.component';
import { STOPickSummaryPageStyle as S } from './STOPickSummaryPage.style';

export const STOPickSummaryPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { assignedSTOItems } = useSelector(
    (state: RootReducerInterface) => state.stoPickItem,
  );

  useEffect(() => {
    if (assignedSTOItems.length === 0) {
      dispatch(getSTOAssignedPickItems());
    }
  }, [dispatch]);

  const { completeSku, incompleteSku } = useMemo(() => {
    function _skuSort(
      sku1: ISTOPickItemWithSummary,
      sku2: ISTOPickItemWithSummary,
    ) {
      // sort by quantity
      if (sku1.pickedQty < sku2.pickedQty) return -1;
      if (sku1.pickedQty > sku2.pickedQty) return 1;

      // sort by sku code
      return sku1.sku_code < sku2.sku_code ? -1 : 1;
    }
    const _completeSku: ISTOPickItemWithSummary[] = [];
    const _incompleteSku: ISTOPickItemWithSummary[] = [];

    assignedSTOItems.forEach((stoItem) => {
      const pickedQty = stoItem.pick_actual.reduce((total, { quantity }) => {
        return total + quantity;
      }, 0);

      if (pickedQty === stoItem.quantity) {
        _completeSku.push({ ...stoItem, pickedQty });
      } else {
        _incompleteSku.push({ ...stoItem, pickedQty });
      }
    });

    return {
      completeSku: _completeSku.sort(_skuSort),
      incompleteSku: _incompleteSku.sort(_skuSort),
    };
  }, [assignedSTOItems]);

  const handleBackToDestination = (): void => {
    navigate(STO_PICK_ROUTES.LIST);
  };

  const handleCompleteStockTransfer = (): void => {
    const itemParams = assignedSTOItems.map((item) => ({
      sto_number: item.sto_number,
      sku_code: item.sku_code,
    }));
    dispatch(
      completeSTOPickAction({
        items: itemParams,
        postAction: () => navigate(STO_PICK_ROUTES.ASSIGN),
      }),
    );
  };

  return (
    <>
      <S.ContentWrapper>
        {/* incomplete */}
        {incompleteSku.length > 0 && (
          <STOSkuList
            stoItems={incompleteSku}
            headerText="Incomplete Stock Transfer"
            headerColor={colors.PRIMARY_YELLOW}
            headerIcon={<ErrorOutlineOutlined />}
            defaultExpanded
          />
        )}

        {/* complete */}
        {completeSku.length > 0 && (
          <STOSkuList
            stoItems={completeSku}
            headerText="Complete Stock Transfer"
          />
        )}
        <S.BlankWhiteSpace />
      </S.ContentWrapper>

      {/* Footer */}
      <S.FooterWrapper>
        <Grid container>
          <Grid item xs={6}>
            <S.SecondaryButton
              onClick={handleBackToDestination}
              data-testid="BtnSTSummaryGoBack"
            >
              GO BACK
            </S.SecondaryButton>
          </Grid>
          <Grid item xs={6}>
            <S.PrimaryButton
              onClick={handleCompleteStockTransfer}
              data-testid="BtnSTSummaryComplete"
            >
              COMPLETE TASK
            </S.PrimaryButton>
          </Grid>
        </Grid>
      </S.FooterWrapper>
    </>
  );
};
